// Kartbibliotek — samlar regionalt kartmaterial med tydlig källangivelse.
//
// Innehållet är än så länge konfigurerat statiskt nedan. Att lägga till en
// ny karta = en post i KARTOR-arrayen. Inga DB-ändringar krävs.
//
// Tre typer av poster stöds:
//   - "interaktiv" — länk till Leaflet-karta (t.ex. /karta)
//   - "område"     — länk till en AreaOverview-sida (visa-knapp) + ev. nedladdning
//   - "pdf"/"jpg"  — direkt nedladdning + förhandsvisning (visa öppnar fil i ny flik)
//
// Källa visas alltid synligt i listposten.

import { Link } from "react-router-dom";
import { MapPin, FileText, Image as ImageIcon, Compass, Download, ExternalLink } from "lucide-react";
import { AREA_LIST } from "@/data/areas";
import { usePageMeta } from "@/utils/usePageMeta";
import AndringsforslagButton from "@/components/AndringsforslagButton";

const TYPE_META = {
  interaktiv: { label: "Interaktiv karta", icon: Compass, color: "text-falu" },
  område:     { label: "Friluftsområde",   icon: MapPin,  color: "text-pine" },
  pdf:        { label: "PDF",              icon: FileText, color: "text-bark" },
  jpg:        { label: "Bildkarta",        icon: ImageIcon, color: "text-bark" },
};

function buildKartor() {
  const rows = [];

  // 1) Interaktiv huvudkarta — alltid överst, leder till /karta
  rows.push({
    id: "interaktiv-huvudkarta",
    titel: "Interaktiv huvudkarta över bygden",
    beskrivning:
      "Karta med markerade företag, föreningar, vårdcentral, butiker och utflyktsmål. " +
      "Filtrera utifrån vad du vill göra: vandra, hitta service, sevärdheter m.m.",
    typ: "interaktiv",
    visa_link: "/karta",
    kalla_namn: "vidalv.live (egen sammanställning)",
    kalla_url: null,
  });

  // 2) Friluftsområden från areas.js (Silsjönäs, Fjällsjö …)
  AREA_LIST.forEach((a) => {
    rows.push({
      id: `omrade-${a.slug}`,
      titel: a.title,
      beskrivning: a.intro.split(". ")[0].replace(/\s+/g, " ").trim() + ".",
      typ: "område",
      visa_link: `/friluftsliv/omrade/${a.slug}`,
      ladda_ner_url: a.downloadUrl || a.mapImage || null,
      ladda_ner_namn: a.downloadName || null,
      ladda_ner_typ: a.mapImageType || null,
      kalla_namn: a.sourceName,
      kalla_url: a.sourceUrl,
    });
  });

  return rows;
}

export default function Kartor() {
  const kartor = buildKartor();

  usePageMeta({
    title: "Kartor – vidalv.live",
    description:
      "Kartbibliotek för Backe, Rossön och bygden vid Fjällsjöälven. Vandringskartor, friluftsområden och interaktiv huvudkarta – alla med tydlig källangivelse.",
    path: "/kartor",
  });

  return (
    <div data-testid="kartor-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Kartor</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3 leading-[1.05]">
        Kartor över bygden
      </h1>
      <p className="text-sm text-bark mb-10 max-w-2xl leading-relaxed">
        Samlat kartmaterial över Backe, Rossön och hela bygden vid Fjällsjöälven – interaktiva kartor,
        friluftsområden och nedladdningsbara vandringskartor. Alla med tydlig källangivelse så att
        man kan gå tillbaka till originalet.
      </p>

      <div className="flex flex-col gap-4">
        {kartor.map((k) => {
          const meta = TYPE_META[k.typ] || TYPE_META.pdf;
          const Icon = meta.icon;
          return (
            <article
              key={k.id}
              data-testid={`kartor-card-${k.id}`}
              className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6"
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className={meta.color} />
                <span className="text-[11px] font-body font-semibold uppercase tracking-[0.2em] text-bark/60">
                  {meta.label}
                </span>
              </div>

              <h2 className="font-heading text-xl font-semibold text-ink mb-2">{k.titel}</h2>
              <p className="text-sm text-bark/80 mb-4 leading-relaxed">{k.beskrivning}</p>

              <div className="flex flex-wrap items-center gap-2 mb-3">
                {k.visa_link && (
                  k.visa_link.startsWith("http") ? (
                    <a
                      href={k.visa_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`kartor-visa-${k.id}`}
                      className="inline-flex items-center gap-1.5 bg-falu hover:bg-falu-hover text-parchment text-sm font-semibold px-4 py-2 rounded-sm no-underline transition-colors"
                    >
                      Visa <ExternalLink size={13} />
                    </a>
                  ) : (
                    <Link
                      to={k.visa_link}
                      data-testid={`kartor-visa-${k.id}`}
                      className="inline-flex items-center gap-1.5 bg-falu hover:bg-falu-hover text-parchment text-sm font-semibold px-4 py-2 rounded-sm no-underline transition-colors"
                    >
                      Visa
                    </Link>
                  )
                )}
                {k.ladda_ner_url && (
                  <a
                    href={k.ladda_ner_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={k.ladda_ner_namn || undefined}
                    data-testid={`kartor-download-${k.id}`}
                    className="inline-flex items-center gap-1.5 border border-warm-border hover:border-bark/50 text-ink text-sm font-semibold px-4 py-2 rounded-sm bg-bone no-underline transition-colors"
                  >
                    <Download size={13} /> Ladda ner
                  </a>
                )}
              </div>

              {k.kalla_namn && (
                <p className="text-xs text-bark/60 mt-1">
                  Källa:{" "}
                  {k.kalla_url ? (
                    <a
                      href={k.kalla_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-falu hover:text-falu-hover no-underline"
                    >
                      {k.kalla_namn} <ExternalLink size={10} className="inline" />
                    </a>
                  ) : (
                    <span>{k.kalla_namn}</span>
                  )}
                </p>
              )}
              <div className="mt-4 pt-3 border-t border-warm-border/60">
                <AndringsforslagButton typ="karta" referensId={k.id} referensNamn={k.titel} />
              </div>
            </article>
          );
        })}
      </div>

      <p className="text-xs text-bark/50 mt-10 leading-relaxed max-w-xl">
        Saknar du en karta? Skicka tips till{" "}
        <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover no-underline">
          redaktion@vidalv.live
        </a>{" "}
        så lägger vi till den med källangivelse.
      </p>
    </div>
  );
}
